import { useState } from "react"
import { Button, Flex, Popconfirm, message } from "antd"
import { DeleteOutlined } from "@ant-design/icons"
import { StoryboardEditor, useAnalyticsEvent } from "~/components"
import type { StoryboardRow } from "~/common/video/storyboard"
import type { UploadedResource } from "~/common/media/uploadedResources"
import styles from "./index.css"

type SavedStoryboardEditorProps = {
  rows: StoryboardRow[]
  mentionableResources: UploadedResource[]
  timeEditing: { errorShotNumbers?: number[]; min: number; max: number }
  visualErrorShotNumbers?: number[]
  onChange: (rows: StoryboardRow[]) => void
  onSave: () => void
}

export const SavedStoryboardEditor = ({
  rows,
  mentionableResources,
  timeEditing,
  visualErrorShotNumbers,
  onChange,
  onSave,
}: SavedStoryboardEditorProps) => {
  const sendAnalytics = useAnalyticsEvent({ prefix: "爆款复刻" })
  const [deleteShotNumber, setDeleteShotNumber] = useState<number | null>(null)
  const deleteShot = (shotNumber: number) => {
    if (rows.length <= 1) {
      void message.error("至少需保留一个分镜")
      setDeleteShotNumber(null)
      return
    }
    onChange(rows.filter((row) => row.shot_number !== shotNumber))
    sendAnalytics("删除分镜")
    onSave()
    setDeleteShotNumber(null)
  }

  return (
    <Flex
      vertical
      gap={16}
      onBlur={onSave}
    >
      {rows.map((row, index) => (
        <Flex
          className={styles.shot}
          key={row.shot_number}
          vertical
        >
          <StoryboardEditor
            onEdit={() => sendAnalytics("编辑分镜")}
            variant="outlined"
            visualErrorShotNumbers={visualErrorShotNumbers}
            actions={
              <Popconfirm
                styles={{
                  container: {
                    width: 360,
                  },
                }}
                open={deleteShotNumber === row.shot_number}
                title={`确认删除分镜 ${row.shot_number}？`}
                description="删除该分镜后无法撤销，该分镜的画面动作描述、口播内容将一并移除。删除分镜后，总时长自动跟随，但请注意现有分镜的衔接时长"
                okText="确认删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                placement="topRight"
                onConfirm={() => deleteShot(row.shot_number)}
                onOpenChange={(open) => {
                  if (!open) {
                    setDeleteShotNumber(null)
                    return
                  }
                  if (rows.length <= 1) {
                    void message.error("至少需保留一个分镜")
                    return
                  }
                  setDeleteShotNumber(row.shot_number)
                }}
              >
                <Button
                  aria-label={`删除分镜 ${row.shot_number}`}
                  className={styles.deleteButton}
                  danger
                  icon={<DeleteOutlined />}
                  type="text"
                />
              </Popconfirm>
            }
            editableFields={["visual_description", "audio_voice"]}
            showSummary={false}
            rows={[row]}
            mentionableResources={mentionableResources}
            timeEditing={{
              ...timeEditing,
              min: index === rows.length - 1 ? timeEditing.min : 0,
            }}
            onChange={([next]) =>
              onChange(
                rows.map((current) =>
                  current.shot_number === next.shot_number ? next : current,
                ),
              )
            }
          />
        </Flex>
      ))}
    </Flex>
  )
}
